/* eslint-disable no-restricted-syntax */
import React from 'react';

import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { RedirectLegacyCopyPageLoader } from '@confluence/redirect-legacy-copy-page';
import { RedirectLegacyViewPageLoader } from '@confluence/redirect-legacy-view-page';
import { processNavigationParams } from '@confluence/ssr-app/entry-points/routes.ts';

import { RedirectOldCreatePage } from './routes/RedirectOldCreatePage.jsx';
import { AdminForgeAppsRoute } from './routes/AdminForgeAppsRoute.tsx';
import { AdminForgeAppsRedirectRoute } from './routes/AdminForgeAppsRedirect.tsx';
import { AdvancedSearchRoute } from './routes/AdvancedSearchRoute.tsx';
import { BetaFeaturesRoute } from './routes/BetaFeaturesRoute.tsx';
import { ExternalShareRoute } from './routes/ExternalShareRoute.tsx';
import { GlobalPageForgeAppsRoute } from './routes/GlobalPageForgeAppsRoute.tsx';
import { GlobalPermissionRoute } from './routes/GlobalPermissionRoute.tsx';
import { AdminCompanyHubPermissionsRoute } from './routes/AdminCompanyHubPermissionsRoute.tsx';
import {
	CompanyHomeBuilderRoute,
	CustomizeNavigationRoute,
} from './routes/CustomizeNavigationRoute.tsx';
import { GlobalAutomationRoute } from './routes/GlobalAutomationRoute.tsx';
import { GlobalPermissionUserDetailsRoute } from './routes/GlobalPermissionUserDetailsRoute.tsx';
import { PdfExportPageRoute } from './routes/PdfExportPageRoute.tsx';
import { PdfExportSpaceRoute } from './routes/PdfExportSpaceRoute.tsx';
import { PeopleDirectoryRoute } from './routes/PeopleDirectoryRoute.tsx';
import { MarketplaceRoute } from './routes/MarketplaceRoute.tsx';
import { SpaceForgeAppsRoute } from './routes/SpaceForgeAppsRoute.tsx';
import { SpaceSettingsForgeAppsRoute } from './routes/SpaceSettingsForgeAppsRoute.tsx';
import { SpaceSettingsArchiveSpaceRoute } from './routes/SpaceSettingsArchiveSpaceRoute.tsx';
import { SpaceSettingsEditorConversionRoute } from './routes/SpaceSettingsEditorConversionRoute.tsx';
import { SpaceSettingsContentStateRoute } from './routes/SpaceSettingsContentStateRoute.tsx';
import { SpaceSettingsRelatedPagesRoute } from './routes/SpaceSettingsRelatedPagesRoute.tsx';
import { SpaceSettingsEditFeaturesRoute } from './routes/SpaceSettingsEditFeaturesRoute.tsx';
import { SpaceSettingsDetailsRoute } from './routes/SpaceSettingsDetailsRoute.tsx';
import { SpaceSettingsAgentContextRoute } from './routes/SpaceSettingsAgentContextRoute.tsx';
import { SpaceLookAndFeelSettingsRoute } from './routes/SpaceLookAndFeelSettingsRoute.tsx';
import { SpaceArchivedPagesRoute } from './routes/SpaceArchivedPagesRoute.tsx';
import { SpaceSuggestionsRoute } from './routes/SpaceSuggestionsRoute.tsx';
import { SpaceSettingsDataClassificationRoute } from './routes/SpaceSettingsDataClassificationRoute.tsx';
import { SpaceSettingsWatermarkConfigurationRoute } from './routes/SpaceSettingsWatermarkConfigurationRoute.tsx';
import { MyCalendarRoute } from './routes/MyCalendarRoute.tsx';
import { MultipleCalendarRoute } from './routes/MultipleCalendarRoute.tsx';
import { EmbeddedMultipleCalendarRoute } from './routes/EmbeddedMultipleCalendarRoute.tsx';
import { SpaceTeamCalendarsRoute } from './routes/SpaceTeamCalendarsRoute.tsx';
import { SpaceEmbeddedSubcalendarRoute } from './routes/SpaceEmbeddedSubcalendarRoute.tsx';
import { SpaceDirectoryRoute } from './routes/SpaceDirectoryRoute.tsx';
import { SpaceSettingsLandingPageRoute } from './routes/SpaceSettingsLandingPageRoute.tsx';
import { SpaceOverviewRoute } from './routes/SpaceOverviewRoute.tsx';
import { CompanyHubRoute } from './routes/CompanyHubRoute.tsx';
import { CompanyHubAnalyticsRoute } from './routes/CompanyHubAnalyticsRoute.tsx';
import { CompanyHubEditRoute } from './routes/CompanyHubEditRoute.tsx';
import { CompanyHubEditorRoute } from './routes/CompanyHubEditorRoute.tsx';
import { CompanyHubSettingsRoute } from './routes/CompanyHubSettingsRoute.tsx';
import { SpacePagesRoute } from './routes/SpacePagesRoute.tsx';
import { BlogHomeRoute } from './routes/BlogHomeRoute.tsx';
import { SpaceBulkToolsRoute } from './routes/SpaceBulkToolsRoute.tsx';
import { TemplateMigrationPreviewRoute } from './routes/TemplateMigrationPreviewRoute.tsx';
import { TemplateEditorRoute } from './routes/TemplateEditorRoute.tsx';
import { OnboardingRoute } from './routes/OnboardingRoute.tsx';
import { ContentHistoryRoute } from './routes/ContentHistoryRoute.tsx';
import { TemplateGalleryRoute } from './routes/TemplateGalleryRoute.tsx';
import { MobileMacroRendererRoute } from './routes/MobileMacroRendererRoute.tsx';
import { MobileTakeoverPageRoute } from './routes/MobileTakeoverPageRoute.tsx';
import { WikiRoute } from './routes/WikiRoute.tsx';
import { HomeRoute } from './routes/HomeRoute.tsx';
import { CreateBlogRoute } from './routes/CreateBlogRoute.tsx';
import { CreatePageRoute } from './routes/CreatePageRoute.tsx';
import { EditPageRoute } from './routes/EditPageRoute.tsx';
import { EditBlogRoute } from './routes/EditBlogRoute.tsx';
import { EditPageOrBlogV2Route } from './routes/EditPageOrBlogV2Route.tsx';
import { EditPageEmbedRoute } from './routes/EditPageEmbedRoute.tsx';
import { EditPageSmartCreateRoute } from './routes/EditPageSmartCreateRoute.tsx';
import { AIPlannerConfluenceBridgeRoute } from './routes/AIPlannerConfluenceBridgeRoute.js';
import { SpaceSettingsAutomationRoute } from './routes/SpaceSettingsAutomationRoute.tsx';
import { SpaceCustomContentListRoute } from './routes/SpaceCustomContentListRoute.tsx';
import { CustomContentOnPageRoute } from './routes/CustomContentOnPageRoute.tsx';
import { CustomContentViewRoute } from './routes/CustomContentViewRoute.tsx';
import { PublicLinksSettingsPageRoute } from './routes/PublicLinksSettingsPageRoute.tsx';
import { AnonymousAccessSettingsPageRoute } from './routes/AnonymousAccessSettingsPageRoute.tsx';
import { SpaceManagementPageRoute } from './routes/SpaceManagementPageRoute.tsx';
import { SpaceDefaultsPageRoute } from './routes/SpaceDefaultsPageRoute.tsx';
import { SpaceRolesPageRoute } from './routes/SpaceRolesPageRoute.tsx';
import { SitePermissionsPageRoute } from './routes/SitePermissionsPageRoute.tsx';
import { GuestsSettingsPageRoute } from './routes/GuestsSettingsPageRoute.tsx';
import { AppsPermissionsSettingsPageRoute } from './routes/AppsPermissionsSettingsPageRoute.tsx';
import { JsmAccessSettingsPageRoute } from './routes/JsmAccessSettingsPageRoute.tsx';
import { ContactAdministratorRoute } from './routes/ContactAdministratorRoute.tsx';
import { SpacePermissionsSettingsRoute } from './routes/SpacePermissionsSettingsRoute.tsx';
import { RoleBasedAccessControlHubRoute } from './routes/RoleBasedAccessControlHubRoute.tsx';
import { AdminAnnouncementBannerRendererRoute } from './routes/AdminAnnouncementBannerRendererRoute.tsx';
import { AdminCenterRoute } from './routes/AdminCenterRoute.tsx';
import { AdminCenterSpaceRoute } from './routes/AdminCenterSpaceRoute.tsx';
import { AdminSettingsRoute } from './routes/AdminSettingsRoute.tsx';
import { SpaceSettingsExportSpaceUnavailableRoute } from './routes/SpaceSettingsExportSpaceUnavailableRoute.tsx';
import { BulkSpacePermissionsRoute } from './routes/BulkSpacePermissionsRoute.tsx';
import { AdminTeamCalendarsRoute } from './routes/AdminTeamCalendarsRoute.tsx';
import { AdminTeamControlsRoute } from './routes/AdminTeamControlsRoute.tsx';
import { AdminConfigurationRoute } from './routes/AdminConfigurationRoute.tsx';
import { AdminDefaultSpaceLogoConfigRoute } from './routes/AdminDefaultSpaceLogoConfigRoute.tsx';
import { AdminHeaderAndFooterRoute } from './routes/AdminHeaderAndFooterRoute.tsx';
import { AdminTemplatesAndBlueprintsRoute } from './routes/AdminTemplatesAndBlueprintsRoute.tsx';
import { AdminNotificationsRoute } from './routes/AdminNotificationsRoute.tsx';
import { AdminCreateWithRovoRoute } from './routes/AdminCreateWithRovoRoute.tsx';
import { PerformanceInsightsRoute } from './routes/AdminPerformanceRoute.tsx';
import { AdminSpaceReportsRoute } from './routes/AdminSpaceReportsRoute.tsx';
import { AdminCodeMacroConfigurationRoute } from './routes/AdminCodeMacroConfigurationRoute.tsx';
import { AdminColorSchemeRoute } from './routes/AdminColorSchemeRoute.tsx';
import { AdminAccessRequestsRoute } from './routes/AdminAccessRequestsRoute.tsx';
import { AdminSecurityConfigurationRoute } from './routes/AdminSecurityConfigurationRoute.tsx';
import { AdminAccessLinksRoute } from './routes/AdminAccessLinksRoute.tsx';
import { AdminAccessLinksConfigurationRoute } from './routes/AdminAccessLinksConfigurationRoute.tsx';
import { AdminApplicationNavigatorRoute } from './routes/AdminApplicationNavigatorRoute.tsx';
import { AdminMacroUsageRoute } from './routes/AdminMacroUsageRoute.tsx';
import { AdminTrashedSpacesRoute } from './routes/AdminTrashedSpacesRoute.tsx';
import { AdminConfluenceChatsIntegrationRoute } from './routes/AdminConfluenceChatsIntegrationRoute.tsx';
import { ConfluenceMsTeamsSignInConfirmationRoute } from './routes/ConfluenceMsTeamsSignInConfirmationRoute.tsx';
import { AdminImportSpacesRoute } from './routes/AdminImportSpacesRoute.tsx';
import { AdminJiraMacroRepairRoute } from './routes/AdminJiraMacroRepairRoute.tsx';
import { AdminThemesRoute } from './routes/AdminThemesRoute.tsx';
import { AdminPdfExportRoute } from './routes/AdminPdfExportRoute.tsx';
import { AdminWatermarkConfigurationRoute } from './routes/AdminWatermarkConfigurationRoute.tsx';
import { SpaceManagerRoute } from './routes/SpaceManagerRoute.tsx';
import { SpaceMembersRoute } from './routes/SpaceMembersRoute.tsx';
import { AdminSiteSpacePermissionsRoute } from './routes/AdminSiteSpacePermissionsRoute.tsx';
import { RedirectToSpaceOverview } from './routes/RedirectToSpaceOverview.tsx';
import { RedirectToHomeWithQuery } from './routes/RedirectToHomeWithQuery.tsx';
import { ActionRedirectLegacyRoute } from './routes/ActionRedirectLegacyRoute.tsx';
import { ResumeDraftLegacyRoute } from './routes/ResumeDraftLegacyRoute.tsx';
import { WhiteboardRoute } from './routes/WhiteboardRoute.tsx';
import { SlidesRoute } from './routes/SlidesRoute.tsx';
import { FolderViewRoute } from './routes/FolderViewRoute.tsx';
import { EmbedViewRoute } from './routes/EmbedViewRoute.tsx';
import { DatabaseViewRoute } from './routes/DatabaseViewRoute.tsx';
import { SpaceIndexRoute } from './routes/SpaceIndexRoute.tsx';
import { CreateContentRoute } from './routes/CreateContentRoute.tsx';
import { PageSplitRoute } from './routes/PageSplitRoute.tsx';
import { ViewPageOrBlogRoute } from './routes/ViewPageOrBlogRoute.tsx';
import { TopicsRoute } from './routes/TopicsRoute.tsx';
import { AskQuestionRoute, QuestionsRoute } from './routes/QuestionsRoute.tsx';
import { QuestionRoute } from './routes/QuestionRoute.tsx';
import { SiteAnalyticsRoute } from './routes/SiteAnalyticsRoute.tsx';
import { SiteAnalyticsPermissionsRoute } from './routes/SiteAnalyticsPermissionsRoute.tsx';
import { PageAnalyticsRoute } from './routes/PageAnalyticsRoute.tsx';
import { SpaceAnalyticsRoute } from './routes/SpaceAnalyticsRoute.tsx';
import { SpaceAnalyticsPermissionsRoute } from './routes/SpaceAnalyticsPermissionsRoute.tsx';
import { LazySpaceLegacyContentRoute } from './routes/SpaceLegacyContentRoute.tsx';
import { LazySpaceAvatarsSettingsRoute } from './routes/SpaceAvatarsSettingsRoute.tsx';
import { LazySpaceStatusAndApprovalsRoute } from './routes/SpaceStatusAndApprovalsRoute.tsx';
import { SpaceSmartCreateRoute } from './routes/SpaceSmartCreateRoute.tsx';
import { UserAccessRoute } from './routes/UserAccessRoute.tsx';
import { LazyExportSpaceRoute } from './routes/ExportSpaceRoute.tsx';
import { GlobalQuestionsRoute } from './routes/GlobalQuestionsRoute.tsx';
import { GlobalQuestionRoute } from './routes/GlobalQuestionRoute.tsx';
import { ImporterDashboardRoute } from './routes/ImporterDashboardRoute.tsx';
import { AdminNBMMigrationRoute } from './routes/AdminNBMMigrationRoute.tsx';
import { AdminDCScriptMigrationRoute } from './routes/AdminDCScriptMigrationRoute.tsx';
import { AttachmentsPageRoute } from './routes/AttachmentsPageRoute.tsx';
import { ChatRoute } from './routes/ChatRoute.tsx';
import { InferredProjectRoute } from './routes/InferredProjectRoute.tsx';
import { PermissionsExportRoute } from './routes/PermissionsExportRoute.tsx';
import { PermissionsCombinationsRoute } from './routes/PermissionsCombinationsRoute.tsx';
import { SystemOperationsRoute } from './routes/SystemOperationsRoute.tsx';
import { AuditLogRoute } from './routes/AuditLogRoute.tsx';
import { AccessLinkRoute } from './routes/AccessLinkRoute.tsx';
import { AccessLinkSpaceSettingsRoute } from './routes/AccessLinkSpaceSettingsRoute.tsx';
import { AccessLinkSpaceSettingsConfigurationRoute } from './routes/AccessLinkSpaceSettingsConfigurationRoute.tsx';

function redirect(name) {
	const redirectComponent = ({ params = {} }) => <Redirection name={name} params={params} />;
	redirectComponent.NAVIGATION_PARAMS = () => false;
	return redirectComponent;
}

/**
 * Please read patterns/routing.md
 */
export const routeComponents = processNavigationParams({
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.BASE.name]: redirect(namedRoutes.WIKI.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.WIKI.name]: WikiRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.HOME.name]: HomeRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MOBILE_TAKEOVER_PAGE.name]: MobileTakeoverPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MOBILE_MACRO_RENDERER.name]: MobileMacroRendererRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.TEMPLATE_GALLERY.name]: TemplateGalleryRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_PAGE.name]: EditPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_BLOG.name]: EditBlogRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_PAGE_V2.name]: EditPageOrBlogV2Route,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_BLOG_V2.name]: EditPageOrBlogV2Route,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_PAGE_EMBED.name]: EditPageEmbedRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_PAGE_SMART_CREATE.name]: EditPageSmartCreateRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.AI_PLANNER_CONFLUENCE_BRIDGE.name]: AIPlannerConfluenceBridgeRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_CONTENT.name]: CreateContentRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_PAGE.name]: CreatePageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_BLOG.name]: CreateBlogRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_PAGE_ACTION_LEGACY.name]: RedirectOldCreatePage,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_BLOG_ACTION_LEGACY.name]: RedirectOldCreatePage,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_PAGE_VARIABLES_ACTION_LEGACY.name]: RedirectOldCreatePage,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.RESUME_DRAFT_LEGACY.name]: ResumeDraftLegacyRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.COPY_PAGE_LEGACY.name]: RedirectLegacyCopyPageLoader,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.DASHBOARD_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPA_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.INDEX_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.VIEW_PAGE_ACTION_LEGACY.name]: RedirectLegacyViewPageLoader,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_OVERVIEW_INDEX_ACTION_LEGACY.name]: RedirectToSpaceOverview,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_INDEX_ACTION_LEGACY.name]: RedirectToSpaceOverview,
	// it should be after "INDEX_ACTION_LEGACY", and can't be the last one, as it will brake navigation to space settings
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ACTION_REDIRECT_LEGACY.name]: ActionRedirectLegacyRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_DIRECTORY.name]: SpaceDirectoryRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_OVERVIEW_REDIRECT_LEGACY.name]: RedirectToSpaceOverview,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_INDEX.name]: SpaceIndexRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_OVERVIEW.name]: SpaceOverviewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.HUB.name]: CompanyHubRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.HUB_EDIT.name]: CompanyHubEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.COMPANY_HUB.name]: CompanyHubRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.COMPANY_HUB_ANALYTICS.name]: CompanyHubAnalyticsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.COMPANY_HUB_EDIT.name]: CompanyHubEditRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.COMPANY_HUB_SETTINGS.name]: CompanyHubSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.GLOBAL_PAGE_FORGE.name]: GlobalPageForgeAppsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_PAGES.name]: SpacePagesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.VIEW_PAGE.name]: ViewPageOrBlogRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PAGE_SPLIT.name]: PageSplitRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.BLOG_HOME.name]: BlogHomeRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.VIEW_BLOG_DATE_LEGACY.name]: ViewPageOrBlogRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.VIEW_BLOG.name]: ViewPageOrBlogRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.WHITEBOARD_HOME.name]: redirect(namedRoutes.SPACE_PAGES.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.WHITEBOARD_BOARD.name]: WhiteboardRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SLIDES_VIEW.name]: SlidesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.DATABASE_VIEW.name]: DatabaseViewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EMBED_VIEW.name]: EmbedViewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.FOLDER_VIEW.name]: FolderViewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CONTACT_ADMINISTRATOR.name]: ContactAdministratorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_CALENDARS.name]: SpaceTeamCalendarsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_CALENDARS_SUBCALENDAR.name]: SpaceTeamCalendarsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_EMBEDDED_SUBCALENDAR.name]: SpaceEmbeddedSubcalendarRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MULTIPLE_SUBCALENDAR.name]: MultipleCalendarRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EMBEDDED_MULTIPLE_SUBCALENDAR.name]: EmbeddedMultipleCalendarRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.DISCOVER.name]: RedirectToHomeWithQuery({ feed: '' }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ALL_UPDATES.name]: RedirectToHomeWithQuery({ feed: 0 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.POPULAR.name]: RedirectToHomeWithQuery({ feed: 1, feedType: 'POPULAR' }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_WORK_HOME.name]: RedirectToHomeWithQuery({ recentTab: 1 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_WORK_DRAFTS.name]: RedirectToHomeWithQuery({ recentTab: 2 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.RECENT_DRAFTS.name]: RedirectToHomeWithQuery({ recentTab: 2 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_WORK_RECENT.name]: RedirectToHomeWithQuery({ recentTab: 1, filter: 'WORKED_ON' }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.RECENT_PAGES.name]: RedirectToHomeWithQuery({ recentTab: 1 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_RECENTLY_VISITED.name]: RedirectToHomeWithQuery({ recentTab: 0 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.RECENT_VISITED.name]: RedirectToHomeWithQuery({ recentTab: 0 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_SAVED_FOR_LATER.name]: RedirectToHomeWithQuery({ recentTab: 3 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.RECENT_SAVED.name]: RedirectToHomeWithQuery({ recentTab: 3 }),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.TEMPLATE_MIGRATION_PREVIEW.name]: TemplateMigrationPreviewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_TEMPLATE.name]: TemplateEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_TEMPLATE_BLUEPRINT.name]: TemplateEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EDIT_TEMPLATE_SPACE.name]: TemplateEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_TEMPLATE.name]: TemplateEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CREATE_TEMPLATE_SPACE.name]: TemplateEditorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADVANCED_SEARCH.name]: AdvancedSearchRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.EXTERNAL_SHARE.name]: ExternalShareRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ACCESS_LINK.name]: AccessLinkRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PEOPLE_DIRECTORY.name]: PeopleDirectoryRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MARKETPLACE_APP_REQUESTS.name]: MarketplaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MARKETPLACE_MANAGE_PROMOTIONS.name]: MarketplaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MARKETPLACE_FIND_APPS.name]: MarketplaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CONTENT_HISTORY.name]: ContentHistoryRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ONBOARDING.name]: OnboardingRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_GLOBAL_AUTOMATION.name]: GlobalAutomationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NEW_GLOBAL_PERMISSIONS.name]: GlobalPermissionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_COMPANY_HUB_PERMISSIONS.name]: AdminCompanyHubPermissionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_COMPANY_HOME_BUILDER.name]: CompanyHomeBuilderRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CUSTOMIZE_NAVIGATION.name]: CustomizeNavigationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NEW_GLOBAL_PERMISSIONS_USER_DETAILS.name]: GlobalPermissionUserDetailsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_EDIT_EXTERNAL_SHARING.name]: PublicLinksSettingsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_ANONYMOUS_ACCESS.name]: AnonymousAccessSettingsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SPACE_MANAGEMENT.name]: SpaceManagementPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SPACE_DEFAULTS.name]: SpaceDefaultsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_GUESTS.name]: GuestsSettingsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_APP_PERMISSIONS.name]: AppsPermissionsSettingsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_JSM_ACCESS.name]: JsmAccessSettingsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SPACE_ROLES.name]: SpaceRolesPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SITE_PERMISSIONS.name]: SitePermissionsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_FORGE_APPS_REDIRECT.name]: AdminForgeAppsRedirectRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_FORGE_APPS.name]: AdminForgeAppsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_BETA_FEATURES.name]: redirect(namedRoutes.ADMIN_NEW_FEATURES.name),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NEW_FEATURES.name]: BetaFeaturesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NOTIFICATIONS.name]: AdminNotificationsRoute,
	[AdminCreateWithRovoRoute.ROUTE_NAME]: AdminCreateWithRovoRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERFORMANCE_INSIGHTS_CPU_CORES.name]: PerformanceInsightsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERFORMANCE_INSIGHTS_NETWORK_SPEED.name]: PerformanceInsightsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERFORMANCE_INSIGHTS_NETWORK_PROTOCOL.name]: PerformanceInsightsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERFORMANCE_INSIGHTS_OPERATING_SYSTEM.name]: PerformanceInsightsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERFORMANCE_INSIGHTS.name]: PerformanceInsightsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SITE_ANALYTICS.name]: SiteAnalyticsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SITE_ANALYTICS_PERMISSIONS.name]: SiteAnalyticsPermissionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_ANALYTICS.name]: SpaceAnalyticsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_ANALYTICS_PERMISSIONS.name]: SpaceAnalyticsPermissionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SMART_CREATE.name]: SpaceSmartCreateRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PAGE_ANALYTICS.name]: PageAnalyticsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_ARCHIVED_PAGES.name]: SpaceArchivedPagesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SUGGESTIONS.name]: SpaceSuggestionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_BULK_TOOLS.name]: SpaceBulkToolsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_LANDING_PAGE.name]: SpaceSettingsLandingPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_LANDING_PAGE_ALT.name]: redirect(
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		namedRoutes.SPACE_SETTINGS_LANDING_PAGE.name,
	),
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_FORGE_APPS.name]: SpaceForgeAppsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_CUSTOM_CONTENT_LIST.name]: SpaceCustomContentListRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_FORGE_APPS.name]: SpaceSettingsForgeAppsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_ARCHIVE_SPACE.name]: SpaceSettingsArchiveSpaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_EDITOR_CONVERSION.name]: SpaceSettingsEditorConversionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_AUTOMATION.name]: SpaceSettingsAutomationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_AVATARS_AND_STATUS.name]: SpaceSettingsContentStateRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_CONTENT_STATE.name]: SpaceSettingsContentStateRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_DATA_CLASSIFICATION.name]: SpaceSettingsDataClassificationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_WATERMARK_CONFIGURATION.name]:
		SpaceSettingsWatermarkConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_EXPORT_SPACE_UNAVAILABLE.name]:
		SpaceSettingsExportSpaceUnavailableRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_EXPORT.name]: LazyExportSpaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_LEGACY_CONTENT.name]: LazySpaceLegacyContentRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_AVATARS.name]: LazySpaceAvatarsSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_STATUS_AND_APPROVALS.name]: LazySpaceStatusAndApprovalsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_RELATED_PAGES.name]: SpaceSettingsRelatedPagesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_EDIT_FEATURES.name]: SpaceSettingsEditFeaturesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_AGENT_CONTEXT.name]: SpaceSettingsAgentContextRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_DETAILS.name]: SpaceSettingsDetailsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_QUESTIONS_NATIVE.name]: QuestionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_QUESTIONS_ASK_NATIVE.name]: AskQuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_QUESTION_NATIVE.name]: QuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.QUESTIONS_NATIVE.name]: GlobalQuestionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.QUESTION_NATIVE.name]: GlobalQuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.QUESTIONS_TOPICS_NATIVE.name]: GlobalQuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.QUESTIONS_TOPIC_NATIVE.name]: GlobalQuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.QUESTIONS_EXPERTS_NATIVE.name]: GlobalQuestionRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.MY_CALENDAR.name]: MyCalendarRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PDF_EXPORT_PAGE.name]: PdfExportPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PDF_EXPORT_SPACE.name]: PdfExportSpaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CUSTOM_CONTENTS_ON_PAGE_LIST.name]: CustomContentOnPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CUSTOM_CONTENT_VIEW.name]: CustomContentViewRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_PERMISSIONS_SETTINGS.name]: SpacePermissionsSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_ACCESS_LINKS.name]: AccessLinkSpaceSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_SETTINGS_ACCESS_LINKS_CONFIGURATION.name]:
		AccessLinkSpaceSettingsConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ROLE_BASED_ACCESS_CONTROL_HUB.name]: RoleBasedAccessControlHubRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_LOOK_AND_FEEL_SETTINGS.name]: SpaceLookAndFeelSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_ANNOUNCEMENT_BANNER_RENDERER.name]: AdminAnnouncementBannerRendererRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_MANAGER.name]: SpaceManagerRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.USER_ACCESS.name]: UserAccessRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CENTER.name]: AdminCenterRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CENTER_SPACE.name]: AdminCenterSpaceRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SETTINGS.name]: AdminSettingsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_BULK_PERMISSIONS.name]: BulkSpacePermissionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SITE_SPACE_PERMISSIONS.name]: AdminSiteSpacePermissionsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_TEAM_CALENDARS.name]: AdminTeamCalendarsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_TEAM_CONTROLS.name]: AdminTeamControlsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CONFIGURATION.name]: AdminConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_DEFAULT_SPACE_LOGO.name]: AdminDefaultSpaceLogoConfigRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_HEADER_AND_FOOTER.name]: AdminHeaderAndFooterRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_TEMPLATES_AND_BLUEPRINTS.name]: AdminTemplatesAndBlueprintsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CODE_MACRO_CONFIGURATION.name]: AdminCodeMacroConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_COLOR_SCHEME.name]: AdminColorSchemeRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_ACCESS_REQUESTS.name]: AdminAccessRequestsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SECURITY_CONFIGURATION.name]: AdminSecurityConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_ACCESS_LINKS.name]: AdminAccessLinksRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_ACCESS_LINKS_CONFIGURATION.name]: AdminAccessLinksConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SPACE_REPORTS.name]: AdminSpaceReportsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_APPLICATION_NAVIGATOR.name]: AdminApplicationNavigatorRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_TRASHED_SPACES.name]: AdminTrashedSpacesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_CONFLUENCE_CHATS_INTEGRATION.name]: AdminConfluenceChatsIntegrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CONFLUENCE_MSTEAMS_SIGNIN_CONFIRMATION.name]:
		ConfluenceMsTeamsSignInConfirmationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_IMPORT_SPACES.name]: AdminImportSpacesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_JIRA_MACRO_REPAIR.name]: AdminJiraMacroRepairRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_MACRO_USAGE.name]: AdminMacroUsageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_THEMES.name]: AdminThemesRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_PDF_EXPORT.name]: AdminPdfExportRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_WATERMARK_CONFIGURATION.name]: AdminWatermarkConfigurationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_DC_SCRIPT_MIGRATION.name]: AdminDCScriptMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_SCANS.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_SCAN.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_UNVERIFIED.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_UNSUPPORTED.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_RUN_TRANSFORMATIONS.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_NBM_MIGRATION_TRANSFORMATIONS.name]: AdminNBMMigrationRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.SPACE_MEMBERS.name]: SpaceMembersRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.TOPICS.name]: TopicsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.IMPORTER_STATUS_DASHBOARD.name]: ImporterDashboardRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ATTACHMENTS_PAGE.name]: AttachmentsPageRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.CHAT.name]: ChatRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.INFERRED_PROJECT_PAGE.name]: InferredProjectRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.INFERRED_PROJECT_PAGE_WITH_ARI.name]: InferredProjectRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERMISSIONS_EXPORT.name]: PermissionsExportRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.PERMISSIONS_COMBINATIONS.name]: PermissionsCombinationsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.ADMIN_SYSTEM_OPERATIONS.name]: SystemOperationsRoute,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	[namedRoutes.NEW_AUDIT_LOG.name]: AuditLogRoute,
});
